import React, { useState, useEffect } from 'react';
import { 
  Users, 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Building,
  Activity,
  Download,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import Avatar from '../components/common/Avatar';
import { useAppContext } from '../contexts/AppContext';
import { Statistics as StatsType, RealtimeActivity } from '../types';

const Statistics: React.FC = () => {
  const { statistics, realtimeActivity } = useAppContext();
  const [timeFilter, setTimeFilter] = useState<'today' | '7days' | '30days'>('today');

  const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const pieData = statistics.topCompanies.map((company, index) => ({
    name: company.company,
    value: company.count,
    color: COLORS[index % COLORS.length]
  }));

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-secondary-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-secondary-900">
                Thống Kê & Báo Cáo
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex space-x-2">
                <Button
                  variant={timeFilter === 'today' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setTimeFilter('today')}
                >
                  Hôm nay
                </Button>
                <Button
                  variant={timeFilter === '7days' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setTimeFilter('7days')}
                >
                  7 ngày
                </Button>
                <Button
                  variant={timeFilter === '30days' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setTimeFilter('30days')}
                >
                  30 ngày
                </Button>
              </div>
              <Button variant="secondary" size="sm">
                <Download size={16} className="mr-2" />
                Xuất báo cáo
              </Button>
              <div className="flex items-center space-x-2">
                <Avatar size="sm" />
                <span className="text-sm font-medium text-secondary-700">BQL</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600">
                  Đang trong tòa nhà
                </p>
                <p className="text-3xl font-bold text-primary-600">
                  {statistics.currentInBuilding}
                </p>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <Users className="w-8 h-8 text-primary-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <ArrowUp className="w-4 h-4 text-success-500 mr-1" />
              <span className="text-success-600 font-medium">+12%</span>
              <span className="text-secondary-500 ml-1">so với hôm qua</span>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600">
                  Lượt vào hôm nay
                </p>
                <p className="text-3xl font-bold text-success-600">
                  {statistics.todayCheckIns}
                </p>
              </div>
              <div className="p-3 bg-success-100 rounded-full">
                <TrendingUp className="w-8 h-8 text-success-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <ArrowUp className="w-4 h-4 text-success-500 mr-1" />
              <span className="text-success-600 font-medium">+8%</span>
              <span className="text-secondary-500 ml-1">so với hôm qua</span>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600">
                  Lượt ra hôm nay
                </p>
                <p className="text-3xl font-bold text-warning-600">
                  {statistics.todayCheckOuts}
                </p>
              </div>
              <div className="p-3 bg-warning-100 rounded-full">
                <TrendingDown className="w-8 h-8 text-warning-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <ArrowDown className="w-4 h-4 text-danger-500 mr-1" />
              <span className="text-danger-600 font-medium">-3%</span>
              <span className="text-secondary-500 ml-1">so với hôm qua</span>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600">
                  Giờ cao điểm
                </p>
                <p className="text-2xl font-bold text-secondary-900">
                  {statistics.peakHour}
                </p>
              </div>
              <div className="p-3 bg-secondary-100 rounded-full">
                <Clock className="w-8 h-8 text-secondary-600" />
              </div>
            </div>
            <div className="mt-4">
              <Badge variant="secondary" size="sm">
                Cao nhất trong ngày
              </Badge>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Hourly Traffic Chart */}
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-secondary-900">
                  Lưu lượng theo giờ
                </h3>
                <div className="flex items-center space-x-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                    <span>Vào</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-warning-500 rounded-full"></div>
                    <span>Ra</span>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={statistics.hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="checkIns" 
                    stackId="1"
                    stroke="#2563eb" 
                    fill="#2563eb" 
                    fillOpacity={0.6}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="checkOuts" 
                    stackId="2"
                    stroke="#f59e0b" 
                    fill="#f59e0b" 
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            {/* Floor Distribution */}
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-secondary-900">
                  Phân bố theo tầng
                </h3>
                <Building className="w-5 h-5 text-secondary-500" />
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statistics.floorBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="floor" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Top Companies */}
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-secondary-900">
                  Công ty được viếng thăm nhiều nhất
                </h3>
              </div>
              <div className="space-y-4">
                {statistics.topCompanies.map((company, index) => (
                  <div key={company.company} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold`}
                           style={{ backgroundColor: COLORS[index % COLORS.length] }}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-secondary-900 text-sm">
                          {company.company}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-secondary-900">{company.count}</p>
                      <p className="text-xs text-secondary-500">lượt</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Real-time Activity */}
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-secondary-900">
                  Hoạt động thời gian thực
                </h3>
                <Activity className="w-5 h-5 text-success-500" />
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {realtimeActivity.map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        activity.action === 'check-in' ? 'bg-success-500' : 'bg-warning-500'
                      }`}></div>
                      <div>
                        <p className="font-medium text-secondary-900 text-sm">
                          {activity.guestName}
                        </p>
                        <p className="text-xs text-secondary-500">
                          {activity.action === 'check-in' ? 'Vào' : 'Ra'} - Tầng {activity.floor}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-secondary-500">
                        {new Date(activity.time).toLocaleTimeString('vi-VN', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
